import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  TextField,
  Alert,
} from '@mui/material';
import { CallSplit as SplitterIcon } from '@mui/icons-material';
import { SPLITTER_PADROES_CORES, PadraoCoresSplitter } from '../../utils/fiberColors';

export const SPLITTER_SAIDAS_OPCOES = [2, 4, 8, 16, 32] as const;

export interface SplitterAddConfig {
  saidas: number;
  padrao_cores: PadraoCoresSplitter;
  saida1_porcentagem?: number;
  saida2_porcentagem?: number;
}

interface SplitterAddModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: SplitterAddConfig) => void;
  isFullscreen?: boolean;
  container?: HTMLElement | null;
}

const SplitterAddModal: React.FC<SplitterAddModalProps> = ({
  open,
  onClose,
  onConfirm,
  isFullscreen = false,
  container,
}) => {
  const [saidas, setSaidas] = useState<number>(2);
  const [padraoCores, setPadraoCores] = useState<PadraoCoresSplitter>('sem_padrao');
  const [saida1Pct, setSaida1Pct] = useState(50);
  const [saida2Pct, setSaida2Pct] = useState(50);
  const [erroPotencia, setErroPotencia] = useState('');

  const menuZIndex = isFullscreen ? 10006 : 1600;
  const selectMenuProps = {
    MenuProps: { disablePortal: true, sx: { zIndex: menuZIndex } },
  };
  const selectFormControlSx = { '& .MuiPopover-root': { zIndex: menuZIndex } };

  useEffect(() => {
    if (open) {
      setSaidas(2);
      setPadraoCores('sem_padrao');
      setSaida1Pct(50);
      setSaida2Pct(50);
      setErroPotencia('');
    }
  }, [open]);

  const handleSaida1Change = (value: number) => {
    if (value >= 1 && value <= 99) {
      setSaida1Pct(value);
      setSaida2Pct(100 - value);
      setErroPotencia('');
    }
  };

  const handleSaida2Change = (value: number) => {
    if (value >= 1 && value <= 99) {
      setSaida2Pct(value);
      setSaida1Pct(100 - value);
      setErroPotencia('');
    }
  };

  const handleConfirm = () => {
    if (saidas === 2) {
      if (saida1Pct + saida2Pct !== 100 || saida1Pct < 1 || saida2Pct < 1) {
        setErroPotencia('A soma das potências das saídas deve ser 100% (mínimo 1% cada).');
        return;
      }
      onConfirm({
        saidas,
        padrao_cores: padraoCores,
        saida1_porcentagem: saida1Pct,
        saida2_porcentagem: saida2Pct,
      });
      return;
    }
    onConfirm({ saidas, padrao_cores: padraoCores });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      container={isFullscreen && container ? container : undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{ zIndex: isFullscreen ? 10005 : 1500 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SplitterIcon sx={{ color: '#2196f3' }} />
          <Typography variant="h6" component="span" sx={{ fontSize: '1rem' }}>
            Adicionar Splitter
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <FormControl fullWidth size="small" required sx={selectFormControlSx}>
            <InputLabel>Quantidade de saídas</InputLabel>
            <Select
              label="Quantidade de saídas"
              value={saidas}
              onChange={(e) => setSaidas(Number(e.target.value))}
              {...selectMenuProps}
            >
              {SPLITTER_SAIDAS_OPCOES.map((qtd) => (
                <MenuItem key={qtd} value={qtd}>
                  1x{qtd}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Splitter 1x2, 1x4, 1x8, 1x16 ou 1x32</FormHelperText>
          </FormControl>

          <FormControl fullWidth size="small" required sx={selectFormControlSx}>
            <InputLabel>Padrão de cores</InputLabel>
            <Select
              label="Padrão de cores"
              value={padraoCores}
              onChange={(e) => setPadraoCores(e.target.value as PadraoCoresSplitter)}
              {...selectMenuProps}
            >
              {SPLITTER_PADROES_CORES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {saidas === 16
                ? 'No 1x16, a saída 9 reinicia na primeira cor.'
                : 'Sem Padrão mantém azul/vermelho no 1x2'}
            </FormHelperText>
          </FormControl>

          {saidas === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Potência de saída (% da potência de entrada)
              </Typography>
              <TextField
                label="Saída 1 (%)"
                type="number"
                size="small"
                value={saida1Pct}
                onChange={(e) => handleSaida1Change(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
              <TextField
                label="Saída 2 (%)"
                type="number"
                size="small"
                value={saida2Pct}
                onChange={(e) => handleSaida2Change(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                Total: {saida1Pct + saida2Pct}% — ajuste para somar 100%
              </Typography>
              {erroPotencia && <Alert severity="error" sx={{ py: 0 }}>{erroPotencia}</Alert>}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Adicionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitterAddModal;
